import { apiFetch } from '@/lib/api-client';
import type {
  CatalogAgeCluster,
  CatalogCombinedPlayback,
  CatalogCombinedTriggerResult,
  CatalogGender,
  CatalogLibraryCard,
  CatalogPlayback,
  CatalogReadContent,
  CatalogUnlockAllResult,
  CatalogUnlockResult,
  CatalogWorldDetail,
  CatalogWorldSummary,
} from './types';

/** GET /api/catalog/worlds — ALL published worlds (browse; ownership-agnostic). */
export function fetchCatalogWorlds(): Promise<CatalogWorldSummary[]> {
  return apiFetch<CatalogWorldSummary[]>('/api/catalog/worlds');
}

/** POST /api/catalog/worlds/:slug/episodes/:n/unlock — spend 1 credit (C1). */
export function unlockCatalogFolge(
  slug: string,
  episodeNumber: number,
): Promise<CatalogUnlockResult> {
  return apiFetch<CatalogUnlockResult>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/episodes/${episodeNumber}/unlock`,
    { method: 'POST' },
  );
}

/**
 * GET /api/catalog/library — the account's owned Wissenswelt cards (one per
 * world with ≥1 unlocked Folge), backend-sorted by latest unlock. Also the
 * capability-probe target for useCatalogEnabled (404 when the backend is
 * dormant). Returns [] when the account owns nothing (feature still live).
 */
export function fetchCatalogLibrary(): Promise<CatalogLibraryCard[]> {
  return apiFetch<CatalogLibraryCard[]>('/api/catalog/library');
}

/** GET /api/catalog/worlds/:slug/detail?ageCluster= — Folgen + unlock state. */
export function fetchCatalogWorldDetail(
  slug: string,
  ageCluster: CatalogAgeCluster,
): Promise<CatalogWorldDetail> {
  return apiFetch<CatalogWorldDetail>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/detail?ageCluster=${ageCluster}`,
  );
}

/**
 * GET /api/catalog/worlds/:slug/episodes/:n/playback — signed audio URL (1h).
 *
 * Optional `childId` (Block 3): when supplied, sent as a query param so the
 * backend can derive the age cluster + child name server-side and stitch a
 * per-child "Hallo Name." intro in front of the episode audio (see backend
 * CatalogQueryService.getCatalogPlaybackUrl). Absent when the household has
 * zero children (no intro) or the flag is off — behavior stays byte-identical.
 */
export function fetchCatalogPlayback(
  slug: string,
  episodeNumber: number,
  ageCluster: CatalogAgeCluster,
  gender: CatalogGender,
  childId?: string,
): Promise<CatalogPlayback> {
  const base = `/api/catalog/worlds/${encodeURIComponent(slug)}/episodes/${episodeNumber}/playback?ageCluster=${ageCluster}&gender=${gender}`;
  const url = childId ? `${base}&childId=${encodeURIComponent(childId)}` : base;
  return apiFetch<CatalogPlayback>(url);
}

/** GET /api/catalog/worlds/:slug/episodes/:n/read — full Folge text. */
export function fetchCatalogRead(
  slug: string,
  episodeNumber: number,
  ageCluster: CatalogAgeCluster,
): Promise<CatalogReadContent> {
  return apiFetch<CatalogReadContent>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/episodes/${episodeNumber}/read?ageCluster=${ageCluster}`,
  );
}

// ── Paket 3 — whole-world unlock + Gesamt-Hörbuch ──────────────────

/**
 * POST /api/catalog/worlds/:slug/unlock-all?ageCluster= — Paket 3 whole-world
 * unlock. Loops the produced Folgen for the given cluster; each newly-unlocked
 * Folge costs 1 credit. Partial success (some unlocked, some skipped_no_credits)
 * is a 200 — the caller reads the counters.
 */
export function unlockCatalogWorld(
  slug: string,
  ageCluster: CatalogAgeCluster,
): Promise<CatalogUnlockAllResult> {
  return apiFetch<CatalogUnlockAllResult>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/unlock-all?ageCluster=${ageCluster}`,
    { method: 'POST' },
  );
}

/**
 * POST /api/catalog/worlds/:slug/combined-audio?ageCluster=&gender= — Paket 3.
 * Trigger the whole-world Gesamt-Hörbuch combine for (world × cluster × gender).
 * Ownership-gated server-side (403 unless every produced Folge is owned). No
 * credits are charged for the combine itself. Idempotent — an in-flight or
 * already-ready row short-circuits (enqueued=false).
 */
export function triggerCatalogCombinedAudio(
  slug: string,
  ageCluster: CatalogAgeCluster,
  gender: CatalogGender,
): Promise<CatalogCombinedTriggerResult> {
  return apiFetch<CatalogCombinedTriggerResult>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/combined-audio?ageCluster=${ageCluster}&gender=${gender}`,
    { method: 'POST' },
  );
}

/**
 * GET /api/catalog/worlds/:slug/combined-audio/playback?ageCluster=&gender=.
 * Returns the current combined-audio status + signed URLs (when ready).
 * Ownership-gated server-side (same 403 as trigger).
 */
export function fetchCatalogCombinedPlayback(
  slug: string,
  ageCluster: CatalogAgeCluster,
  gender: CatalogGender,
): Promise<CatalogCombinedPlayback> {
  return apiFetch<CatalogCombinedPlayback>(
    `/api/catalog/worlds/${encodeURIComponent(slug)}/combined-audio/playback?ageCluster=${ageCluster}&gender=${gender}`,
  );
}
